import { useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { listPage, type Page, type PageRequest } from '@/models/listPage';
import type { ResolvablePath } from '@/models/resolve';
import { fetchSummaries, type SummarySpec } from '@/models/summaries';

/** Ce qui se charge avec la page : des constantes du module, pas des valeurs recréées à chaque rendu */
export interface PagedListOptions {
  /** Relations résolues avec la page (listPage) */
  resolvables?: readonly ResolvablePath[];
  /** Résumé des objets de la page (fetchSummaries) : une requête de plus, en même temps que les relations */
  summary?: SummarySpec;
}

/**
 * Page courante d'une liste paginée côté API. Les résultats précédents restent affichés pendant le chargement
 * de la page suivante (pas de clignotement) ; reloadKey force un rechargement (après création, modification…).
 * request null : rien à charger (ex. aucune société sélectionnée). options : relations et résumé chargés avec la page.
 */
export function usePagedList<T extends ModelConstructor>(
  modelClass: T,
  request: PageRequest | null,
  reloadKey = 0,
  { resolvables = [], summary }: PagedListOptions = {},
) {
  const key = JSON.stringify([request, reloadKey]);
  const [state, setState] = useState<{ key: string; page: Page<InstanceType<T>> | null; error: string | null } | null>(
    null,
  );

  useEffect(() => {
    const [pageRequest] = JSON.parse(key) as [PageRequest | null];
    if (!pageRequest) return;
    let cancelled = false;
    listPage(modelClass, pageRequest, resolvables, summary && ((models) => fetchSummaries(models, summary)))
      .then((page) => !cancelled && setState({ key, page, error: null }))
      .catch((e) => !cancelled && setState({ key, page: null, error: getApiErrorMessage(e) }));

    return () => {
      cancelled = true;
    };
    // resolvables, summary : des constantes du module (cf. ci-dessus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelClass, key]);

  return {
    models: state?.page?.models ?? [],
    totalItems: state?.page?.totalItems ?? 0,
    loading: !!request && state?.key !== key,
    error: state?.key === key ? state.error : null,
  };
}
